"use client";

import { FeedEvents, UserEvents, trackEvent } from "@/analytics";

import ClapReactionButton from "@/components/special/ClapReactionButton";
import { Comedian } from "@/data/comedians";
import { CommentCount } from "@/components/comments";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import FeedCard from "./FeedCard";
import { FeedPost } from "../types";
import { Follow } from "@/components/checkout";
import HideButton from "@/components/common/HideButton";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import VideoSkeleton from "@punchup/ui-common/components/common/VideoSkeleton";

const LiveVideoPlayerAndWaitingRoom = dynamic(
  () =>
    import("@/components/livestreaming/LiveVideoPlayerAndWaitingRoom").then(
      (mod) => mod.LiveVideoPlayerAndWaitingRoom
    ),
  { ssr: false, loading: () => <VideoSkeleton /> }
);
import { Livestream } from "@punchup/data/legacy/livestreaming";
import ShareButton from "@/components/common/ShareButton";
import { SpecialRow } from "@/data/specials";
import VideoFeedHeaderMobile from "./VideoFeedHeaderMobile";
import { VideoJS, VideoPlayerSurface } from "@punchup/video/client";
import { cn } from "@/components/common/utils";
import dayjs from "@punchup/utils/dayjs";
import { memo } from "react";
import { useDeviceDetection } from "@/components/common/hooks";

// Mobile Header Overlay Component
function MobileHeaderOverlay({
  comedian,
  special,
  postAge,
  profileImg,
  userFollowsComedian,
  isLoggedOutState,
  redirectTo,
  followCallback,
  renderTimestamp,
  taggedComedians,
}: {
  comedian: Comedian;
  special: SpecialRow;
  postAge: number;
  profileImg: string | null;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  redirectTo: string;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
  taggedComedians?: import("@/components/feed/types").TaggedComedian[];
}) {
  return (
    <div className="absolute top-0 left-0 right-0 z-20 p-3 flex items-center gap-3 bg-gradient-to-b from-black/80 to-transparent">
      <VideoFeedHeaderMobile
        comedian={comedian}
        special={special}
        postAge={postAge}
        profileImg={profileImg}
        userFollowsComedian={userFollowsComedian}
        isLoggedOutState={isLoggedOutState}
        redirectTo={redirectTo}
        followCallback={followCallback}
        renderTimestamp={renderTimestamp}
        taggedComedians={taggedComedians}
      />
    </div>
  );
}

// Video Title Overlay Component - positioned above video controls
function VideoTitleOverlay({
  title,
  specialUrl,
}: {
  title: string;
  specialUrl: string;
}) {
  const containerClasses = cn([
    "absolute",
    "bottom-8",
    "left-0",
    "right-0",
    "z-20",
    "px-4",
    "py-3",
    "bg-gradient-to-t",
    "from-black/80",
    "to-transparent",
    "hover:underline",
    "hover:underline-offset-2",
    "duration-200",
    "ease-in-out",
    "decoration-[transparent]",
    "hover:decoration-white",
    "transition-[text-decoration]",
  ]);

  const titleClasses = cn([
    "text-base",
    "font-normal",
    "text-white",
    "line-clamp-1",
    "truncate",
  ]);

  return (
    <Link
      href={specialUrl}
      onClick={() => trackEvent(FeedEvents.CLICKED_TITLE_VIDEO_CARD)}
      className={containerClasses}
      data-testid="video-feed-card-title-link"
    >
      <h2 className={titleClasses}>{title}</h2>
    </Link>
  );
}

// Video Content Area Component
function VideoContentArea({
  livestream,
  post,
  special,
  specialImg,
  comedian,
  videoSrc,
  previewUrl,
  posterImg,
  previewImg,
  userHasAccessToSpecial,
  muted,
  handleMutedChange,
  playing,
}: {
  livestream: Livestream | null;
  post: FeedPost;
  special: SpecialRow;
  specialImg: string;
  comedian: Comedian;
  videoSrc: string;
  previewUrl: string | null;
  posterImg: string;
  previewImg: string | null;
  userHasAccessToSpecial: boolean;
  muted: boolean;
  handleMutedChange: (value: boolean) => void;
  playing: boolean;
}) {
  return (
    <div className="w-full h-full relative">
      {livestream && post.livestream?.signedVideoToken ? (
        <LiveVideoPlayerAndWaitingRoom
          special={special}
          livestream={livestream}
          placeholderImageUrl={specialImg}
          signedToken={post.livestream?.signedVideoToken}
          allowStreamTools={false}
        />
      ) : !previewUrl ? (
        <Image
          src={specialImg}
          alt={`${special.title} by ${comedian.display_name}`}
          width={798}
          height={462}
          className="w-full h-full object-cover bg-gray-300"
        />
      ) : userHasAccessToSpecial ? (
        <VideoJS
          bigPlayButton={false}
          special={special}
          poster={posterImg}
          context={{ comedian, recommendedVideos: [] }}
          lastPlaybackLocation={0}
          onSpecialChange={() => {}}
          src={videoSrc}
          isPreview={false}
          surface={VideoPlayerSurface.Feed}
          inline
          disableOverlays
          muted={muted}
          handleMutedChange={handleMutedChange}
          playing={playing}
          isVertical={true}
          className="w-full h-full"
          playerProps={{
            showSkipButtons: false,
            showPlaybackRate: false,
            showVolumePanel: false,
            showFullscreenToggle: false,
            showTimeRemaining: false,
          }}
        />
      ) : previewUrl && previewImg ? (
        <VideoJS
          bigPlayButton={false}
          special={special}
          poster={previewImg ?? ""}
          context={{ comedian, recommendedVideos: [] }}
          lastPlaybackLocation={0}
          onSpecialChange={() => {}}
          src={previewUrl ?? ""}
          isPreview={true}
          surface={VideoPlayerSurface.Feed}
          inline
          disableOverlays
          muted={muted}
          handleMutedChange={handleMutedChange}
          playing={playing}
          isVertical={true}
          className="w-full h-full"
          playerProps={{
            showSkipButtons: false,
            showPlaybackRate: false,
            showVolumePanel: false,
            showFullscreenToggle: false,
            showTimeRemaining: false,
          }}
        />
      ) : null}
    </div>
  );
}

interface VideoFeedCardVerticalProps {
  post: FeedPost;
  special: SpecialRow;
  comedian: Comedian;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  muted: boolean;
  handleMutedChange: (value: boolean) => void;
  playing: boolean;
  videoRefCallback: (element: HTMLDivElement) => (() => void) | void;
  // Data props
  videoSrc: string;
  profileImg: string | null;
  previewUrl: string | null;
  previewImg: string | null;
  userSubscribedToComedian: boolean | null;
  livestream: Livestream | null;
  userPurchasedSpecial: boolean | null;
  subscriptionPrice: number | null | undefined;
  // Computed values
  posterImg: string;
  specialImg: string;
  userHasAccessToSpecial: boolean;
  isCommentsEnabled: boolean;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
  postAge: number;
  specialUrl: string;
  redirectTo: string;
  hideEnabled?: boolean;
  onHide?: (postId: string) => void;
}

function VideoFeedCardVertical({
  post,
  special,
  comedian,
  userFollowsComedian,
  isLoggedOutState,
  muted,
  handleMutedChange,
  playing,
  videoRefCallback,
  videoSrc,
  profileImg,
  previewUrl,
  previewImg,
  livestream,
  posterImg,
  specialImg,
  userHasAccessToSpecial,
  isCommentsEnabled,
  followCallback,
  renderTimestamp,
  postAge,
  specialUrl,
  redirectTo,
  hideEnabled = false,
  onHide,
}: VideoFeedCardVerticalProps) {
  // Device detection setup
  const { device } = useDeviceDetection();
  const isMobile = device === "mobile";

  // FeedCard container styles
  const feedCardContainer = cn(["rounded-md", "flex", "flex-col", "h"]);

  // Video container styles
  const videoContainer = cn([
    "w-full",
    // "bg-green-500",
    "h-[92%]",
    "relative",
  ]);

  const videoActionsContainer = cn([
    "w-full",
    // "bg-red-500",
    "h-[8%]",
    "flex",
    "items-center",
    "gap-3",
    "px-4",
    "py-1.5",
  ]);

  return (
    <FeedCard
      variant="vertical-mobile"
      className={feedCardContainer}
      style={{
        height: isMobile
          ? "calc(100vh - var(--navbar-height) - var(--header-height))"
          : undefined,
      }}
    >
      <div
        className={videoContainer}
        key={`mobile-vertical-${special.id}`}
        ref={videoRefCallback}
      >
        <MobileHeaderOverlay
          comedian={comedian}
          special={special}
          postAge={postAge}
          profileImg={profileImg}
          userFollowsComedian={userFollowsComedian}
          isLoggedOutState={isLoggedOutState}
          redirectTo={redirectTo}
          followCallback={followCallback}
          renderTimestamp={renderTimestamp}
          taggedComedians={post.taggedComedians}
        />

        <VideoTitleOverlay title={special.title} specialUrl={specialUrl} />

        <VideoContentArea
          livestream={livestream}
          post={post}
          special={special}
          specialImg={specialImg}
          comedian={comedian}
          videoSrc={videoSrc}
          previewUrl={previewUrl}
          posterImg={posterImg}
          previewImg={previewImg}
          userHasAccessToSpecial={userHasAccessToSpecial}
          muted={muted}
          handleMutedChange={handleMutedChange}
          playing={playing}
        />
      </div>
      <div className={videoActionsContainer}>
        <ClapReactionButton
          special={special}
          comedian={comedian}
          authenticated={!isLoggedOutState}
          className="h-[32px]"
        />
        {isCommentsEnabled && (
          <CommentCount
            videoId={special.id}
            showAvatars={true}
            className="text-sm"
            href={specialUrl}
          />
        )}
        <ShareButton
          iconColor="stroke-[#3F3F3F]"
          className="h-[32px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
          label="Share"
          shareData={{
            title: comedian.display_name || "",
            text: `Check out ${comedian.display_name} on Punchup!`,
            url: specialUrl,
          }}
          location="feed-video-card"
          iconHeight={18}
          iconWidth={18}
        />
        {hideEnabled && (
          <HideButton
            specialId={special.id}
            postId={post.id}
            onHide={onHide}
            location="feed-video-card"
            label="Hide"
            iconColor="stroke-[#3F3F3F]"
            className="h-[32px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
            iconHeight={18}
            iconWidth={18}
          />
        )}
      </div>
    </FeedCard>
  );
}

export default memo(VideoFeedCardVertical);
